import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FileText, Clock } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const STATUS_LABEL = { new: "Novo", contacted: "Contactado", converted: "Convertido", archived: "Arquivado" };
const STATUS_CLASS = {
    new: "bg-blue-100 text-blue-700 border-blue-200",
    contacted: "bg-amber-100 text-amber-700 border-amber-200",
    converted: "bg-emerald-100 text-emerald-700 border-emerald-200",
    archived: "bg-zinc-100 text-zinc-600 border-zinc-200",
};

export default function ContaPedidos() {
    const [items, setItems] = useState(null);

    useEffect(() => {
        api.get("/account/cart-requests")
            .then(({ data }) => setItems(data))
            .catch((err) => {
                toast.error(formatApiError(err?.response?.data?.detail) || "Erro ao carregar pedidos.");
                setItems([]);
            });
    }, []);

    return (
        <div data-testid="conta-pedidos">
            <h2 className="font-heading text-2xl font-extrabold text-primary">Os meus pedidos</h2>
            <p className="text-sm text-muted-foreground">Histórico de configurações submetidas via simulador.</p>

            {items === null && <div className="mt-8 text-sm text-muted-foreground">A carregar...</div>}

            {items && items.length === 0 && (
                <div className="mt-8 rounded-2xl border-2 border-dashed border-border bg-white p-10 text-center" data-testid="pedidos-empty">
                    <div className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-secondary/40 text-primary">
                        <FileText className="h-6 w-6" />
                    </div>
                    <h3 className="mt-4 font-heading text-xl font-extrabold text-primary">Sem pedidos por agora</h3>
                    <p className="mt-2 text-sm text-muted-foreground max-w-md mx-auto">
                        Submeta uma configuração no <Link to="/simulador" className="text-primary underline font-bold">simulador</Link> para começar.
                    </p>
                </div>
            )}

            {items && items.length > 0 && (
                <ul className="mt-8 space-y-3" data-testid="pedidos-list">
                    {items.map((req) => (
                        <li key={req.id} className="rounded-2xl border border-border bg-white p-5 hover:border-primary/30 transition" data-testid={`pedido-${req.id}`}>
                            <div className="flex items-start justify-between gap-4 flex-wrap">
                                <div className="min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <h3 className="font-heading text-lg font-extrabold text-primary">
                                            Carrinho {req.size === "1.98m" ? "Ômega 1,98m" : "Alfa 1,35m"}
                                        </h3>
                                        <span className={`rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${STATUS_CLASS[req.status] || STATUS_CLASS.new}`}>
                                            {STATUS_LABEL[req.status] || req.status}
                                        </span>
                                    </div>
                                    <div className="mt-2 flex items-center gap-2 text-xs text-muted-foreground">
                                        <Clock className="h-3 w-3" /> {fmt(req.created_at)}
                                    </div>
                                    {(req.addons?.length > 0) && (
                                        <p className="mt-3 text-sm text-primary"><span className="font-bold">Equipamento:</span> {req.addons.join(", ")}</p>
                                    )}
                                    {req.canopy && (
                                        <p className="mt-1 text-sm text-primary"><span className="font-bold">Cobertura:</span> {req.canopy.type} ({req.canopy.color})</p>
                                    )}
                                    {req.brand_name && (
                                        <p className="mt-1 text-sm text-primary"><span className="font-bold">Marca:</span> {req.brand_name}</p>
                                    )}
                                    {req.notes && <p className="mt-2 text-xs text-muted-foreground">{req.notes}</p>}
                                </div>
                            </div>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}

function fmt(s) {
    if (!s) return "—";
    try {
        const d = typeof s === "string" ? new Date(s) : s;
        return d.toLocaleString("pt-PT", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
    } catch {
        return String(s);
    }
}
